import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, Input, Output, EventEmitter } from '@angular/core';
import { CdkDragDrop, CdkDropList, copyArrayItem, moveItemInArray, CdkDrag } from '@angular/cdk/drag-drop';
import { Observable, Subscription, debounceTime, distinctUntilChanged, map } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldExpression, term, toString as luceneToString } from 'lucene';
import { NgFor, NgIf } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';

import { User } from '../../../models/user';
import { UserService } from '../../../services/user.service';
import { BatchContentWidget, BatchesService, FieldOfBatchContentWidget } from '../../../services/batches.service';
import { pageTransitions } from '../../../animations/page-transitions';
import { OrderByDirection, OrderBy } from '../../../utils/constant';
import { LoadingSpinnerComponent } from '../../utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../directives/virtual-scroller.directive';
import { SlideButtonComponent } from '../../slide-button/slide-button.component';

import { WidgetContentChecker } from './available-widgets';

const FIELD_TO_RETRIEVE = <const> {
	customerArticle: 'customerArticleReference',
	steelgrade: 'steelgrade',
	steelgradeDescription: 'steelgradeDescription'
};

@Component({
	selector: 'copo-rts-content-selector',
	templateUrl: './rts-content-selector.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		SlideButtonComponent,

		MatFormFieldModule,
		MatIconModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		CdkDropList,
		VirtualScrollerDirective,
		CdkScrollable,
		NgFor,
		CdkDrag,
		NgIf,
		LoadingSpinnerComponent
	]
})
export class RtsContentSelectorComponent implements OnDestroy, OnInit, WidgetContentChecker {
	@ViewChild('selectedContainerList') selectedContainerList?: CdkDropList;
	@ViewChild('availableContainterList') availableContainterList?: CdkDropList;
	@Input() currentContent?: unknown;
	@Output() readonly content = new EventEmitter<unknown>(true);
	readonly MAX_ITEM_COUNT = 8;
	user: User;
	batchDisplay = 'customerArticleReference';
	sortWay = OrderByDirection.DESC;
	filter: string | undefined;
	searchTextControl = new FormControl('');
	customerArticleSelected = false;
	fieldToRetrieve: FieldOfBatchContentWidget[] = [FIELD_TO_RETRIEVE.customerArticle];
	fieldToSort: keyof BatchContentWidget = FIELD_TO_RETRIEVE.customerArticle;
	search = '';

	selectedItemList: BatchContentWidget[] = [];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly userService: UserService,
		private readonly batchesService: BatchesService
	) {
		this.user = this.userService.user;
		this.updateFieldToSortAndRetrieve();

		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined> value;
			if (searchText === undefined) {
				return;
			}
			this.search = searchText;
			this.filter = searchText ? this.createLuceneFilter(searchText) : undefined;
		});
	}

	get dataId() {
		const dataId = `batches-${
			this.sortWay
		}-${
			String(this.fieldToRetrieve)
		}-${
			this.fieldToSort
		}-${
			this.batchDisplay
		}`;
		return this.filter === undefined ? dataId : `${ dataId }-${ this.filter }`;
	}

	drop(event: CdkDragDrop<BatchContentWidget[]>) {
		if (event.previousContainer === this.availableContainterList && event.previousContainer === event.container) {
			return;
		}
		if (event.container === this.selectedContainerList) {
			if (event.previousContainer === event.container) {
				moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
				this.content.emit(this.selectedItemList.length > 0 ? this.getContent() : undefined);
				return;
			}
			if (this.isSelectedContinsItem(event.previousContainer.data[event.previousIndex])) {
				return;
			}
			copyArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
			this.content.emit(this.selectedItemList.length > 0 ? this.getContent() : undefined);
			return;
		}
		if (event.container === this.availableContainterList) {
			this.selectedItemList.splice(event.previousIndex, 1);
			this.content.emit(this.selectedItemList.length > 0 ? this.getContent() : undefined);
		}
	}

	getContent() {
		const steelgradeObj = { steelgrade: <{ code: string; translation: string }[]> [] };
		const customerArticle = { customerArticleReference: <string[]> [] };
		for (const value of this.selectedItemList) {
			if (!value.customerArticleReference && !value.steelgrade) {
				continue;
			}
			if (value.customerArticleReference !== undefined) {
				customerArticle.customerArticleReference.push(value.customerArticleReference);
			} else if (value.steelgrade !== undefined && value.steelgradeDescription !== undefined) {
				steelgradeObj.steelgrade
					.push({ code: value.steelgrade, translation: value.steelgradeDescription });
			}
		}

		return this.customerArticleSelected ? customerArticle : steelgradeObj;
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<BatchContentWidget[]>) => void) {
		this.filter = this.search ? this.createLuceneFilter(this.search) : undefined;
		provideData(
			this.batchesService.getBatchesWidgetContent(
				this.fieldToRetrieve,
				this.filter,
				index,
				limit,
				new OrderBy(this.fieldToSort, OrderByDirection.ASC),
				true
			)
				.pipe(map(res => res.results))
		);
	}

	onToggleChange(eventChange: boolean) {
		this.customerArticleSelected = !eventChange;

		this.selectedItemList = [];
		this.content.emit();
		this.updateFieldToSortAndRetrieve();
	}

	maxItemLimit() {
		return this.selectedItemList.length >= this.MAX_ITEM_COUNT;
	}

	isSelectedContinsItem(batchWidget: BatchContentWidget) {
		for (const value of this.selectedItemList) {
			if (value.steelgrade === batchWidget.steelgrade
				&& value.customerArticleReference === batchWidget.customerArticleReference) {
				return true;
			}
		}
		return false;
	}

	createLuceneFilter(searchText: string) {
		const search = `*${ term.escape(searchText) }*`;
		let fieldExpression: FieldExpression = {
			field: 'steelgradeDescription',
			term: search,
			quoted: false
		};

		if (this.customerArticleSelected) {
			fieldExpression = {
				field: 'customerArticleReference',
				term: search,
				quoted: false,
			};
		}
		return luceneToString(fieldExpression);
	}

	ngOnInit() {
		this.updateSelectedListContainer();
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
	}

	private updateSelectedListContainer() {
		if (!this.currentContent) {
			return;
		}

		const content = <ContentField> this.currentContent;
		const contentType = <keyof ContentField> Object.keys(content)[0];

		switch (contentType) {
		case 'customerArticleReference':
			this.customerArticleSelected = true;
			this.updateFieldToSortAndRetrieve();
			for (const value of content.customerArticleReference) {
				this.selectedItemList.push({ customerArticleReference: value });
			}
			break;
		case 'steelgrade':
			for (const value of content.steelgrade) {
				this.customerArticleSelected = false;
				this.updateFieldToSortAndRetrieve();
				this.selectedItemList.push({
					steelgradeDescription: value.translation,
					steelgrade: value.code
				});
			}
			break;
		}
		if (this.selectedItemList.length > 0) {
			this.content.emit(this.getContent());
		}
	}

	private updateFieldToSortAndRetrieve() {
		this.fieldToRetrieve = this.customerArticleSelected ? [FIELD_TO_RETRIEVE.customerArticle]
			: [FIELD_TO_RETRIEVE.steelgrade, FIELD_TO_RETRIEVE.steelgradeDescription];
		this.fieldToSort = this.customerArticleSelected ? FIELD_TO_RETRIEVE.customerArticle
			: FIELD_TO_RETRIEVE.steelgradeDescription;
	}
}

interface ContentField {
	steelgrade: Steelgrade[];
	customerArticleReference: string[];
}

interface Steelgrade {
	code: string;
	translation: string;
}
